/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { createRef, forwardRef, Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormControl } from '../../../FormControl/FormControl';
import { FormControlOption } from '../../../FormControl/FormControl.defs';
import { FormControlProps } from '../../../FormControl/FormControl.props';
import { FORM_CONTROL_TYPE_RADIO } from '../../../FormControl/types/FormControlType.types';
import { FormField } from '../../../FormField';
import { CheckboxRadioProps, checkboxRadioPropsDefaults } from '../CheckboxRadio.props';
import { CHECKBOX_RADIO_TYPE_PROMINENT } from '../CheckboxRadio.types';

/**
 * SDS Radio Buttons component.
 */
export const RadioButtons = forwardRef((props: CheckboxRadioProps, ref: Ref<HTMLInputElement>): JSX.Element => {
  const {
    radioCallback,
    classes,
    disabled,
    tabOverLabels,
    fieldLabel,
    hideLabel,
    id,
    options,
    readOnly,
    value,
    variant,
    ...rest
  } = {
    ...checkboxRadioPropsDefaults,
    ...props,
  };

  const inputRef = ref as Ref<HTMLInputElement>;
  const listClass = `sds-form-control-list sds-form-control--${variant}`;

  const [chosenOption, setChosenOption] = useState<FormControlOption | null>(null);
  // Set the new option and communicate this back up the chain
  const setOption = useCallback(
    (option: FormControlOption) => {
      // Prevent triggering updates and callbacks if there is a chosen option and it's already selected.
      if (chosenOption && option.key === chosenOption.key) {
        return;
      }
      setChosenOption(option);

      // Protect against null or undefined options being sent back
      if (option) {
        radioCallback && radioCallback(option);
      }
    },
    [chosenOption, radioCallback, setChosenOption],
  );

  // Create radio refs
  const currentFocusIndex = useRef<number>(-1);
  const radioRefs = useMemo<Array<React.RefObject<HTMLButtonElement>>>(
    () =>
      Array.from(
        {
          length: options.length,
        },
        () => createRef<HTMLButtonElement>(),
      ),
    [options],
  );

  // Handles moving the focus between radio buttons in group
  const moveRadioFocus = (itemIndex: number): void => {
    currentFocusIndex.current = itemIndex;
    const radioButton = radioRefs[itemIndex].current as HTMLButtonElement;
    if (!radioButton) {
      return;
    }
    radioButton.focus();
    setOption(options[itemIndex]);
  };

  // Handles keyboard events that occur on radio buttons
  const handleRadioKeydown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    const { key } = event;

    // Handle only these keys
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (navigationKeys.includes(key)) {
      event.preventDefault();

      // Create mutable value that initializes as the currentFocusIndex value
      let newFocusIndex = currentFocusIndex.current;

      // Controls the new index to focus
      if (key === 'ArrowUp' || key === 'ArrowLeft') {
        newFocusIndex -= 1;
      } else if (key === 'ArrowDown' || key === 'ArrowRight') {
        newFocusIndex += 1;
      } else if (key === 'Home') {
        newFocusIndex = 0;
      } else if (key === 'End') {
        newFocusIndex = radioRefs.length - 1;
      }

      if (newFocusIndex > radioRefs.length - 1) {
        newFocusIndex = 0;
      } else if (newFocusIndex < 0) {
        newFocusIndex = radioRefs.length - 1;
      }

      // Set focus on the new value
      moveRadioFocus(newFocusIndex);
      return;
    }
  };

  const onOptionClick = (optionIndex: number): void => {
    moveRadioFocus(optionIndex);
  };
  const findFirstNonDisabledOptionIndex = (): number => {
    return options.findIndex((option) => !option.disabled);
  };
  const getOptions = (): Array<JSX.Element> => {
    return options.map((option, index): JSX.Element => {
      const optionClass = clsx(`sds-form-control-option`, {
        [`disabled`]: disabled || option.disabled,
        [`readOnly`]: readOnly,
      });

      const radioProps = { ...props } as FormControlProps;
      radioProps.svg = option.svg;
      radioProps.optionIndex = index;
      radioProps.customClick = onOptionClick;
      radioProps.customKeyDown = handleRadioKeydown;
      radioProps.disabled = disabled;
      radioProps.radioRef = radioRefs[index];
      radioProps.option = option;
      radioProps.dataId = option.dataId;
      radioProps.type = FORM_CONTROL_TYPE_RADIO;
      radioProps.tabIndex = -1;
      radioProps.tabOverLabels = tabOverLabels;

      if (option.checked && option !== chosenOption) {
        setChosenOption(option);
      }
      if (chosenOption) {
        if (chosenOption.value === option.value) {
          radioProps.checked = true;
          radioProps.tabIndex = 0;
        }
      } else if (index === findFirstNonDisabledOptionIndex()) {
        radioProps.tabIndex = 0;
      }
      return (
        <li className="sds-form-control-item" key={option.key} role="none">
          <FormControl {...radioProps} classes={optionClass} ref={inputRef} />
        </li>
      );
    });
  };

  useEffect((): void => {
    if (value) {
      const defaultOptionIndex = options.findIndex((option) => option.value === value);
      setOption(options[defaultOptionIndex]);
    }
  }, [options, setOption, value]);

  // Configure field
  const formFieldProps = { ...props };
  formFieldProps.classes = clsx(`sds-form-controls`, classes);

  return (
    <FormField {...formFieldProps}>
      <div aria-labelledby={`sds-form-label--${id}`} className="sds-radio-group" id={id} role="radiogroup" {...rest}>
        {variant === CHECKBOX_RADIO_TYPE_PROMINENT ? (
          <div className="sds-field-container">
            <ul className={listClass} role="none">
              {getOptions()}
            </ul>
          </div>
        ) : (
          <ul className={listClass} role="none">
            {getOptions()}
          </ul>
        )}
      </div>
    </FormField>
  );
});
